import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, DropdownButton, FormControl } from 'react-bootstrap';
import { loadingContent } from '../../components/EmptyStates.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import {
  deleteChangeImpact,
  retrieveChangeImpact,
  searchChangeImpactParticipants,
  selectChangeImpact,
  selectChangeImpactParticipants,
  selectRetrieveChangeImpactState,
} from '../../modules/changeImpacts.js';
import ChangeImpactHeader from '../../components/headers/PageHeaders/Project/ChangeImpactHeader.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import ChangeImpactSummary from '../../components/projects/ChangeImpact/ChangeImpactSummary.jsx';
import { retrieveChangeImpactTypes } from '../../modules/changeImpactTypes.js';
import {
  retrieveProject,
  selectProject,
  selectRetrieveProjectState,
} from '../../modules/projects.js';
import ChangeImpactStateReview from '../../components/projects/ChangeImpact/ChangeImpactStateReview.jsx';
import {
  retrieveEmployees,
  selectRetrieveEmployeesState,
} from '../../modules/employees.js';
import TasksTable from '../../components/projects/ChangeImpact/TasksTable.jsx';
import Empty from '../../components/Empty.jsx';
import {
  retrieveTasks,
  selectRetrieveTasksState,
  selectTasks,
} from '../../modules/tasks.js';
import { calculatePercentage } from '../../lib/utils.js';
import {
  EditChangeImpactLink,
  NewTaskLink,
} from '../../components/links/index.jsx';
import CustomMenuItem from '../../components/CustomMenuItem.jsx';
import { PlusButtonIcon } from '../../components/icons/PlusButtonIcon.jsx';
import { EditBlueIcon } from '../../components/icons/EditBlueIcon.jsx';
import ChangeImpactWhoIsImpacted from '../../components/projects/ChangeImpact/ChangeImpactWhoIsImpacted.jsx';
import {
  retrieveChangeImpactStakeholderParticipations,
  searchChangeImpactStakeholderParticipations,
  selectChangeImpactStakeholders,
} from '../../modules/stakeholderParticipations.js';
import StakeholdersUploadStatus from '../../components/projects/ChangeImpact/StakeholdersUploadStatus.jsx';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import {
  retrievePortfolios,
  selectPortfolio,
} from '../../modules/portfolio.js';
import { DeleteBlueIcon } from '../../components/icons/DeleteBlueIcon.jsx';
import ImpactedUploadStatus from '../../components/projects/ChangeImpact/ImpactedUploadStatus.jsx';
import ImportDataProgressContainer from '../ImportDataProgressContainer.jsx';
import CiImportDataError from '../../components/projects/ChangeImpact/CiImportDataError.jsx';

const defaultParticipantsState = {
  impactedEmployees: [],
  impactedGroups: [],
  impactedExternalEntities: [],
};

const mapStateToProps = (state, ownProps) => {
  const project = selectProject(ownProps.projectId)(state);
  return {
    retrieveProjectSuccess: selectRetrieveProjectState(state).success,
    retrieveChangeImpactSuccess: selectRetrieveChangeImpactState(state).success,
    retrieveEmployeesSuccess: selectRetrieveEmployeesState(state).success,
    retrieveTasksSuccess: selectRetrieveTasksState(state).success,
    changeImpact: selectChangeImpact(ownProps.changeImpactId)(state),
    tasks: selectTasks(state, ownProps),
    portfolio: project?.portfolioId
      ? selectPortfolio(project.portfolioId)(state)
      : null,
    projectId: ownProps.projectId,
    changeImpactId: ownProps.changeImpactId,
    stakeholderParticipations: selectChangeImpactStakeholders(state),
    changeImpactParticipants:
      selectChangeImpactParticipants(ownProps.changeImpactId)(state) ||
      defaultParticipantsState,
    apiUrl: state.config.apiUrl,
    project,
  };
};

const mapDispatchToProps = {
  deleteChangeImpact,
  retrieveProject,
  retrieveChangeImpact,
  retrieveEmployees,
  retrieveTasks,
  retrieveChangeImpactTypes,
  searchChangeImpactParticipants,
  searchChangeImpactStakeholderParticipations,
  retrieveChangeImpactStakeholderParticipations,
  retrievePortfolios,
};

const ChangeImpactDetailContainer = (props) => {
  const {
    retrieveProjectSuccess,
    retrieveChangeImpactSuccess,
    portfolio,
    retrieveProject,
    retrieveChangeImpact,
    searchChangeImpactParticipants,
    retrieveChangeImpactTypes,
    retrieveEmployees,
    retrieveTasks,
    retrieveChangeImpactStakeholderParticipations,
    searchChangeImpactStakeholderParticipations,
    stakeholderParticipations,
    changeImpactParticipants,
    retrievePortfolios,
    project,
    changeImpact,
    deleteChangeImpact,
    tasks,
    retrieveTasksSuccess,
    projectId,
    changeImpactId,
    apiUrl,
  } = props;

  const { downloadBlob } = useFetchFile();

  const [completedTasks, setCompletedTasks] = useState(0);

  const onDownloadErrorsCsv = (type) => {
    // 'stakeholders' || 'impacted'
    const url =
      type === 'impacted'
        ? 'group_structure_errors_csv'
        : 'stakeholder_structure_errors_csv';
    const fileName =
      type === 'impacted'
        ? 'errors-groups-and-people-structure_csv'
        : 'errors-stakeholders-structure CSV';
    downloadBlob(
      apiUrl,
      `change_impacts/${changeImpactId}/${url}`,
      null,
      fileName,
      'csv'
    );
  };

  const onDownloadImportDataErrorsCsv = () => {
    downloadBlob(
      apiUrl,
      `projects/${projectId}/change_impacts/bulk_upload/errors_csv`,
      null,
      'errors-errors-import-data CSV',
      'csv'
    );
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    searchChangeImpactParticipants(changeImpactId, query);
    searchChangeImpactStakeholderParticipations(changeImpactId, query);
  };

  const onDelete = () => {
    if (window.confirm('Are you sure you want to delete this change impact?')) {
      const submitPromise = deleteChangeImpact(changeImpact.id);

      submitPromise.then(() =>
        pushRoute('projects/change-impacts', { projectId })
      );
    }
  };

  useEffect(() => {
    retrieveProject(projectId);
    retrieveChangeImpact(changeImpactId);
    retrieveChangeImpactTypes();
    retrieveEmployees();
    retrieveChangeImpactStakeholderParticipations(changeImpactId);
    retrieveTasks(changeImpactId);
    retrievePortfolios();
    searchChangeImpactParticipants(changeImpactId, '');
  }, []);

  useEffect(() => {
    tasks
      ? setCompletedTasks(
          tasks.filter((task) => task.status === 'Completed').length
        )
      : 0;
  }, [tasks]);

  if (!retrieveProjectSuccess || !retrieveChangeImpactSuccess || !tasks) {
    return <Empty simplified content={loadingContent} />;
  }
  return (
    <div className="relative">
      <ImportDataProgressContainer
        projectId={project.id}
        status={project.processingChangeImpactUpload}
      />
      <ChangeImpactHeader changeImpact={changeImpact} project={project} />
      <SubHeader flexJustify small>
        <div className="flex">
          <h2>Overview: {changeImpact.title}</h2>
        </div>
        <div className="flex">
          {project.manageProjectEntities ? (
            project.archived ? (
              <Button
                onClick={() =>
                  window.alert(getOrgTermsContent('archivedProjectMessage'))
                }
                bsStyle="success"
              >
                <PlusButtonIcon />
                Add Task
              </Button>
            ) : (
              <NewTaskLink
                projectId={projectId}
                changeImpactId={changeImpactId}
              >
                <Button bsStyle="success">
                  <PlusButtonIcon />
                  Add Task
                </Button>
              </NewTaskLink>
            )
          ) : null}
          {project.manageProjectEntities ? (
            <div className="actions m-l-16">
              <DropdownButton
                noCaret
                title=""
                id="id"
                className="btn-more btn btn-default"
              >
                {project.archived ? (
                  <React.Fragment>
                    <span>
                      <CustomMenuItem
                        onClick={() =>
                          window.alert(
                            getOrgTermsContent('archivedProjectMessage')
                          )
                        }
                      >
                        <span className="flex flex-align-center">
                          <EditBlueIcon />
                          <span className="m-l-8">Edit Change Impact</span>
                        </span>
                      </CustomMenuItem>
                      <CustomMenuItem divider />
                    </span>
                    <span>
                      <CustomMenuItem
                        onClick={() =>
                          window.alert(
                            getOrgTermsContent('archivedProjectMessage')
                          )
                        }
                      >
                        <span className="flex flex-align-center">
                          <DeleteBlueIcon />
                          <span className="m-l-8">Delete</span>
                        </span>
                      </CustomMenuItem>
                      <CustomMenuItem divider />
                    </span>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <EditChangeImpactLink
                      projectId={projectId}
                      changeImpactId={changeImpact.id}
                    >
                      <CustomMenuItem>
                        <span className="flex flex-align-center">
                          <EditBlueIcon />
                          <span className="m-l-8">Edit Change Impact</span>
                        </span>
                      </CustomMenuItem>
                      <CustomMenuItem divider />
                    </EditChangeImpactLink>
                    <span>
                      <CustomMenuItem onClick={() => onDelete()}>
                        <span className="flex flex-align-center">
                          <DeleteBlueIcon />
                          <span className="m-l-8">Delete</span>
                        </span>
                      </CustomMenuItem>
                      <CustomMenuItem divider />
                    </span>
                  </React.Fragment>
                )}
              </DropdownButton>
            </div>
          ) : null}
        </div>
      </SubHeader>

      <CiImportDataError project={project} />

      <ChangeImpactSummary
        changeImpact={changeImpact}
        project={project}
        portfolio={portfolio}
      />

      <ImpactedUploadStatus
        changeImpact={changeImpact}
        project={project}
        onDownloadErrorsCsv={() => onDownloadErrorsCsv('impacted')}
      />

      <StakeholdersUploadStatus
        changeImpact={changeImpact}
        project={project}
        onDownloadErrorsCsv={() => onDownloadErrorsCsv('stakeholders')}
      />

      <SubHeader flexJustify small>
        <h2>Who is Impacted</h2>
        <FormControl
          style={{ maxWidth: '540px' }}
          placeholder="Search..."
          type="text"
          className="search-icon m-r-30"
          onChange={handleSearch}
        />
      </SubHeader>
      <ChangeImpactWhoIsImpacted
        participants={changeImpactParticipants}
        stakeholderParticipations={stakeholderParticipations}
      />

      <SubHeader small>
        <h2>Change Impact Analysis</h2>
      </SubHeader>
      <ChangeImpactStateReview changeImpact={changeImpact} />

      <SubHeader flexJustify small>
        <div className="flex flex-1">
          <h2>Tasks</h2>
          <strong className="header-item completion bordered text-muted">
            Total:&nbsp;
            <span className="text-primary">{tasks.length}</span>
          </strong>
          <strong className="header-item completion bordered text-muted">
            Completed:&nbsp;
            <span className="text-primary">
              {calculatePercentage(completedTasks, tasks.length)}%
            </span>
            <span className="text-primary">
              &nbsp;&nbsp;
              {completedTasks}/{tasks.length}
            </span>
          </strong>
        </div>
        {project.manageProjectEntities ? (
          project.archived ? (
            <Button
              onClick={() =>
                window.alert(getOrgTermsContent('archivedProjectMessage'))
              }
              bsStyle="success"
            >
              <PlusButtonIcon />
              Add Task
            </Button>
          ) : (
            <NewTaskLink projectId={projectId} changeImpactId={changeImpactId}>
              <Button bsStyle="success">
                <PlusButtonIcon />
                Add Task
              </Button>
            </NewTaskLink>
          )
        ) : null}
      </SubHeader>
      {retrieveTasksSuccess ? (
        <TasksTable
          tasks={tasks}
          changeImpactId={changeImpactId}
          projectId={projectId}
          archived={project.archived}
          manageProjectEntities={project.manageProjectEntities}
        />
      ) : null}
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeImpactDetailContainer);
